// Mirrors functions/src/utils/requestLimitLock.ts. Keep both in step
import type { SongRequest } from '../services/requests';

export const REQUEST_LIMITS_OFF_VALUE = 999;
export const MAX_REQUEST_LIMIT = 50;

const DEFAULT_MAX_REQUESTS_PER_USER = 5;

export function isRequestLimitLocked(event: { requestsStartedAt?: string } | null | undefined): boolean {
  return Boolean(event?.requestsStartedAt);
}

// The saved limit: the lowest value a client write may send once requests have started
export function requestLimitFloor(
  event: { requestsStartedAt?: string; settings?: { maxRequestsPerUser?: number } } | null | undefined,
): number | null {
  if (!isRequestLimitLocked(event)) return null;
  return event?.settings?.maxRequestsPerUser ?? DEFAULT_MAX_REQUESTS_PER_USER;
}

const ACTIVE_REQUEST_STATUSES: ReadonlyArray<SongRequest['status']> = ['pending', 'queued'];

type CountableRequest = Pick<SongRequest, 'status' | 'superRequest' | 'isOverflow' | 'practiceMode' | 'requestedBy'>;

function isCountedRequest(request: CountableRequest, djId: string): boolean {
  if (!ACTIVE_REQUEST_STATUSES.includes(request.status)) return false;
  if (request.superRequest === true || request.isOverflow === true || request.practiceMode === true) return false;
  const oderId = request.requestedBy?.oderId;
  return typeof oderId === 'string' && oderId.length > 0 && oderId !== djId;
}

export function highestActiveRequestCount(requests: CountableRequest[], djId: string): number {
  const countsByAttendee = new Map<string, number>();
  for (const request of requests) {
    if (!isCountedRequest(request, djId)) continue;
    const oderId = request.requestedBy.oderId;
    countsByAttendee.set(oderId, (countsByAttendee.get(oderId) ?? 0) + 1);
  }
  return Math.max(0, ...countsByAttendee.values());
}

export function requestLimitFloorOf(highestCount: number): number {
  return Math.max(1, highestCount);
}

export function requestLimitFloorText(highestCount: number): string {
  const floor = requestLimitFloorOf(highestCount);
  if (highestCount === 0) return `Lowest you can set: ${floor}. No attendee has songs waiting.`;
  const songs = highestCount === 1 ? 'song' : 'songs';
  return `Lowest you can set: ${floor}. An attendee has ${highestCount} ${songs} waiting.`;
}

export function requestLimitRefusalText(floor: number): string {
  return `An attendee has ${floor} songs waiting, so the limit can't go below ${floor}.`;
}

export function startingRequestLimit(djDefaultLimit: number, floor: number | null): number {
  return Math.min(MAX_REQUEST_LIMIT, Math.max(djDefaultLimit, floor ?? 1, 1));
}

export function requestLimitCorrectedSaveText(savedLimit: number, typedLimit: number): string {
  if (typedLimit < 1) return `Saved, but Max Requests is set to ${savedLimit}, the lowest you can set right now.`;
  return `Saved, but Max Requests is ${savedLimit}, not the ${typedLimit} you typed. ${savedLimit} is the lowest you can set right now.`;
}
